import React from 'react';
import styled from 'styled-components';
import Papa from 'papaparse';
import { useTranslation } from 'react-i18next';

import { useSnackbarContext } from 'common/context';
import { ActionButton } from 'common/components/buttons/ActionButton';
import {
    useInvitationsExportLazyQuery,
    IInvitationsExportQuery,
} from 'graphql/types';
import { getDisplayDate } from 'common/utils';
import { LoadingSpinner } from 'common/components/icons';

interface IProps {
    className?: string;
}

const BaseInvitationsExport = ({ className }: IProps) => {
    const { t } = useTranslation();
    const { setSnackbarState } = useSnackbarContext();

    const createAndDownloadCsv = (data: IInvitationsExportQuery) => {
        const formattedData = data.invitations.objects.map((invitation) => ({
            [t('formfields.firstName')]: invitation.employee.firstName,
            [t('formfields.lastName')]: invitation.employee.lastName,
            [t('formfields.email')]: invitation.employee.email,
            [t('formfields.personnelNumber')]:
                invitation.employee.personnelNumber,
            [t('employee.detail.invitationId')]: invitation.externalId,
            [t('employee.detail.sentAt')]: getDisplayDate(invitation.lastSent),
            [t('employee.status')]: invitation.statusDisplay,
            [t('employee.detail.budget')]: invitation.budget.name,
        }));

        const csvData = Papa.unparse(formattedData);

        const blob = new Blob([csvData], { type: 'text/csv' });

        const elem = window.document.createElement('a');
        elem.href = window.URL.createObjectURL(blob);
        elem.download = 'export.csv';
        document.body.appendChild(elem);
        elem.click();
        document.body.removeChild(elem);
    };

    const [fetchExportData, { loading }] = useInvitationsExportLazyQuery({
        fetchPolicy: 'no-cache',
        onError: (error) =>
            setSnackbarState({
                message: error.message,
                variant: 'error',
            }),
        onCompleted: createAndDownloadCsv,
    });

    return (
        <div className={className}>
            <ActionButton onClick={() => fetchExportData()}>
                {loading ? <LoadingSpinner /> : t('employee.buttonExport')}
            </ActionButton>
        </div>
    );
};

export const InvitationsExport = styled(BaseInvitationsExport)`
    ${LoadingSpinner} {
        width: 40px;
        height: 40px;
        stroke: ${({ theme }) => theme.palette.background.paper};
    }
`;
